/* Pack Fresh / Common Lands — first-party placement attribution beacon.
 *
 * Logs CLICKS on product cards that sit in a tracked placement (home rails +
 * collection-page featured rows), with their POSITION (slot) so we can tell
 * first-4 (above the fold) from the ones you have to swipe to. Fire-and-forget
 * via navigator.sendBeacon — a one-way POST, no response read, no blocking.
 *
 * Also logs every PAGE VIEW and CART ADD (site activity) so the owner can replay one
 * customer's visit on the analytics Customers tab.
 *
 * Privacy/security: no PII in the payload. We send a random per-browser id, the
 * signed-in customer id (hashed server-side the instant it lands, never stored raw),
 * and facts already visible in the page (path, title, product id, rail, slot). The
 * endpoint is write-only and validates/rate-limits/allow-lists server-side; the data
 * is only ever read from owner-authenticated consoles.
 */
(function () {
  var ENDPOINT = 'https://siteman.pack-fresh.com/api/track';

  // Anonymous per-browser session id (not tied to any customer identity).
  var KEY = 'pf_sess';
  var sess;
  try {
    sess = localStorage.getItem(KEY);
    if (!sess) {
      sess = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(KEY, sess);
    }
  } catch (e) { sess = 'na'; }

  // Logged-in Shopify customer id (null for guests). Sent same-origin to the beacon, which
  // hashes it server-side — never stored raw, never leaves as PII.
  var cid = (typeof window.__pf_cid !== 'undefined' && window.__pf_cid) ? window.__pf_cid : null;

  function beacon(el, ev) {
    try {
      var payload = {
        session: sess,
        cid: cid,
        event: ev || 'click',
        product: el.getAttribute('data-pf-product'),
        rail: el.getAttribute('data-pf-rail'),
        slot: el.getAttribute('data-pf-slot'),
        placement: el.getAttribute('data-pf-track')
      };
      if (!payload.product || payload.slot == null) return;
      var blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      if (navigator.sendBeacon) navigator.sendBeacon(ENDPOINT, blob);
    } catch (e) { /* never let tracking break the page */ }
  }

  // SITE ACTIVITY — one beacon per page view (and again on a back/forward bfcache restore,
  // which never re-runs this script), so a customer's visit can be replayed page by page on
  // the analytics Customers tab. Path + allow-listed query (server drops everything else),
  // the page's title and type, the product on a PDP, and the referrer's host+path only.
  function siteBeacon(ev, extra) {
    try {
      var p = {
        session: sess, cid: cid, site: ev,
        pt: window.__pf_pt || null,
        path: location.pathname, qs: location.search,
        title: (document.title || '').slice(0, 300),
        product: window.__pf_pid || null,
        variant: (location.search.match(/[?&]variant=(\d+)/) || [])[1] || null,
        ref: document.referrer || null
      };
      if (extra) for (var k in extra) p[k] = extra[k];
      if (navigator.sendBeacon) navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(p)], { type: 'text/plain' }));
    } catch (e) { /* never let tracking break the page */ }
  }
  siteBeacon('pageview');
  window.addEventListener('pageshow', function (e) { if (e.persisted) siteBeacon('pageview', { ref: null }); });

  // Capture-phase so we record the click even if the card stops propagation.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!link) return;
    var tracked = link.closest('[data-pf-track]');
    if (tracked) beacon(tracked, 'click');
  }, true);

  // Stamp the anonymous session onto the cart so the eventual order carries it (pf_sess note
  // attribute) and the orders/create webhook can attribute the purchase to wherever this
  // shopper first clicked in. Fires on EVERY add — inline quick-add OR add from a product
  // page — so click-through buyers get attributed too, not just quick-adds off the card.
  function stampSession() {
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { pf_sess: sess } }),
        keepalive: true
      });
    } catch (e) { /* never block the add */ }
  }

  // Beacon an add that happened off a product page — no positional card in scope, so we send
  // only session + product. The server reconstructs the slot/placement from this session's
  // most-recent click on that product (the same join the orders webhook uses for revenue),
  // so an add after clicking through to the PDP still lands in the position funnel.
  function beaconProductAdd(pid) {
    if (!pid) return;
    try {
      var payload = { session: sess, cid: cid, event: 'add_to_cart', product: pid };
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
      }
    } catch (e) { /* never let tracking break the page */ }
  }

  // Capture-phase submit — fires before the theme's product-form.js preventDefault + fetch.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    stampSession();                                    // every add now carries the session
    // Every add, wherever it came from, lands on the customer's site timeline.
    try {
      var vIn = form.querySelector('[name="id"]');
      var pWrap = (form.closest && (form.closest('[data-pf-product]') || form.closest('product-info[data-product-id]'))) || null;
      siteBeacon('add_to_cart', {
        variant: vIn ? vIn.value : null,
        product: pWrap ? (pWrap.getAttribute('data-pf-product') || pWrap.getAttribute('data-product-id')) : (window.__pf_pid || null)
      });
    } catch (e2) {}
    var tracked = form.closest && form.closest('[data-pf-track]');
    if (tracked) {
      beacon(tracked, 'add_to_cart');                  // inline add: position known from the card
      return;
    }
    // PDP add: read the product id off the existing <product-info> wrapper (main-product.liquid).
    var info = form.closest && form.closest('product-info[data-product-id]');
    if (info) beaconProductAdd(info.getAttribute('data-product-id'));
  }, true);

  // VIEW tracking — batched. IntersectionObserver pools every tracked card that scrolls
  // into view (deduped, once each) into a Map; we send ONE beacon per pageview on exit.
  // Views are a firehose (up to ~72/page) so we never fire per-card — one batch keeps the
  // load sane, and the server bulk-inserts into the isolated merch_view_event table.
  if ('IntersectionObserver' in window) {
    var viewed = new Map();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var k = el.getAttribute('data-pf-product') + '|' + el.getAttribute('data-pf-rail') + '|' + el.getAttribute('data-pf-slot');
        if (!viewed.has(k)) viewed.set(k, {
          product: el.getAttribute('data-pf-product'),
          rail: el.getAttribute('data-pf-rail'),
          slot: el.getAttribute('data-pf-slot'),
          placement: el.getAttribute('data-pf-track')
        });
        io.unobserve(el);                 // count each card once
        if (viewed.size >= 150) flushViews();   // safety valve on very long sessions
      });
    }, { threshold: 0.5 });               // 50% of the card visible = a real view
    function observeAll() {
      document.querySelectorAll('[data-pf-track]').forEach(function (el) {
        if (!el.__pfo) { el.__pfo = 1; io.observe(el); }
      });
    }
    observeAll();
    setTimeout(observeAll, 2000);         // catch any rails rendered just after load
    function flushViews() {
      if (!viewed.size) return;
      var payload = { session: sess, cid: cid, views: Array.from(viewed.values()) };
      viewed = new Map();
      try { navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'text/plain' })); } catch (e) {}
    }
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flushViews(); });
    window.addEventListener('pagehide', flushViews);
  }
})();
